<template>
  <div class="e-multilanguage">
    <component
      v-model.trim="activeLang!.value"
      ref="inputRef"
      :is="component"
      :label="label"
      :focused="isShownMenu"
      :rules="[...(rules || []), ...(commonRules || [])]"
      :error="hasInsideErrors"
      :readonly="props.readonly"
      no-resize
    >
      <template #append-inner>
        <div
          class="e-multilanguage-field__flag"
          :class="{ 'e-multilanguage-field__flag--error': hasInsideErrors }"
          @mousedown.stop
          @mouseup.stop
          @click.prevent.stop="menuUpdate(true)"
        >
          <v-img v-if="activeLang" :src="getLanguageIcon(activeLang.langCode)" width="16" />
        </div>
      </template>
    </component>
    <v-menu
      v-model="isShownMenu"
      activator="parent"
      :open-on-click="false"
      :open-on-focus="false"
      :close-on-content-click="false"
      :content-class="[
        'e-multilanguage__content',
        {
          isTextarea: textarea,
          'e-multilanguage__content--error-offset': inputRef && inputRef?.input?.isValid === false,
          'e-multilanguage__content--error': hasInsideErrors || (inputRef && inputRef?.input?.isValid === false),
        },
      ]"
      @update:model-value="onMenuUpdate"
    >
      <v-list-item v-for="(item, index) in languagesValues" class="no-hover" :key="index">
        <component
          v-model.trim="item.value"
          :is="component"
          :error-messages="errorMessages(item)"
          :autofocus="index === 0"
          :readonly="props.readonly"
          no-resize
        >
          <template #append>
            <div
              :class="{ transparentBg: !isActiveItem(item.langCode) }"
              class="e-multilanguage-field__flag"
              @click="changeActiveLang(index)"
            >
              <v-img :src="getLanguageIcon(item.langCode)" width="16" />
            </div>
          </template>
        </component>
      </v-list-item>
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { VInput } from 'vuetify/components';
import type { MultilanguageValue } from '@/core/utils/languages';
import { EInput, ETextarea } from '@/core/components/ui';
import { getDefaultMultilanguageValues, getLanguageIcon } from '@/core/utils/languages';
import { deepEquals } from '@/core/utils/object';

const props = defineProps<{
  modelValue: MultilanguageValue[] | null;
  label?: string;
  textarea?: boolean;
  rules?: VInput['rules'];
  commonRules?: VInput['rules'];
  readonly?: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: MultilanguageValue[]): void;
}>();

const inputRef = ref();

const languagesValues = ref<MultilanguageValue[]>([]);

const hasLocaleValue = (langCode: MultilanguageValue['langCode']) =>
  languagesValues.value.find((l) => l.langCode === langCode);

const filteredLanguagesValues = toRef(() => languagesValues.value.filter((l) => !!l.value));

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      props.modelValue.forEach((locale) => {
        if (!hasLocaleValue(locale.langCode)) {
          languagesValues.value.push(locale);
        } else {
          const lang = languagesValues.value.find((l) => l.langCode === locale.langCode);
          if (lang) {
            lang.value = locale.value;
          }
        }
      });
    }

    const defaultLocaleValues = getDefaultMultilanguageValues();

    defaultLocaleValues.forEach((locale) => {
      if (!hasLocaleValue(locale.langCode)) {
        languagesValues.value.push(locale);
      }
    });
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  languagesValues,
  () => {
    if (!deepEquals(filteredLanguagesValues.value, props.modelValue)) {
      emit('update:modelValue', filteredLanguagesValues.value);
    }
  },
  {
    deep: true,
  },
);

const component = props.textarea ? ETextarea : EInput;

const isShownMenu = ref(false);

const activeItemLangCode = ref('RU');

const isActiveItem = (langCode: MultilanguageValue['langCode']) => langCode === activeItemLangCode.value;

const activeLang = toRef(() => languagesValues.value.find((item) => isActiveItem(item.langCode)));

const changeActiveLang = (index: number) => {
  if (!languagesValues.value[index]) {
    return;
  }

  activeItemLangCode.value = languagesValues.value[index].langCode;
};

const menuUpdate = (value: boolean) => {
  isShownMenu.value = value;
};

const findActiveLang = () => {
  if (activeLang.value?.value) {
    return;
  }

  const newActiveLang = languagesValues.value.find((l) => !!l.value);

  if (newActiveLang) {
    activeItemLangCode.value = newActiveLang.langCode;
  }
};

const insideInputsRules = (item: MultilanguageValue) => {
  if (item.langCode === activeItemLangCode.value) {
    return [...(props.rules || []), ...(props.commonRules || [])];
  }

  return props.commonRules || [];
};

const errorMessages = (item: MultilanguageValue) => {
  const rules = insideInputsRules(item) || [];

  return rules.map((rule) => (rule as (v: string) => string | boolean)(item.value)).filter((x) => x !== true);
};

const hasInsideErrors = computed(() => {
  const r = props.commonRules || [];

  return languagesValues.value.some((item) => {
    const errors = r.map((rule) => (rule as (v: string) => string | boolean)(item.value));

    return !!errors.filter((x) => x !== true).length;
  });
});

const onMenuUpdate = (value: boolean) => {
  if (!value) {
    findActiveLang();
  }
};
</script>

<style scoped lang="scss">
.e-multilanguage {
  &-field {
    &__flag {
      z-index: 9;

      padding: 10px 8px;

      background-color: $primary-light-color;
      border-radius: 6px;

      cursor: pointer;

      &.transparentBg {
        background-color: transparent;
      }

      &--error {
        background-color: $alert-bg-color;
      }

      @include hover {
        background-color: $primary-tonal-color;
      }
    }
  }
}

:deep(.e-multilanguage__content) {
  padding: 16px 0 8px !important;

  &[style*='--v-overlay-anchor-origin: bottom left'] {
    padding: 8px 0 8px !important;

    transform: translateY(-42px);
  }

  &.isTextarea {
    &[style*='--v-overlay-anchor-origin: bottom left'] {
      transform: translateY(-124px);
    }
  }

  .v-list-item {
    &:first-child {
      padding-top: 0;
    }
  }
}

:deep(.e-multilanguage__content--error-offset) {
  &[style*='--v-overlay-anchor-origin: bottom left'] {
    transform: translateY(-58px);
  }
}

:deep(.e-multilanguage__content--error-offset.isTextarea) {
  &[style*='--v-overlay-anchor-origin: bottom left'] {
    transform: translateY(-138px);
  }
}
</style>
